import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { withRetry } from '../utils/errorHandling';
import { DEFAULT_REGULAR_DEPOSIT_DUE_VALUE } from '../utils/depositDueDuration';
import { 
  LoginCredentials, 
  RegisterData, 
  AuthResponse,
  Login2faChallenge,
  LoginApiResult,
  isLogin2faChallenge,
  User,
  UserRole,
  ApiResponse,
  Invitation,
  InvitationFormData,
  GiftCard,
  GiftCardFormData,
  GiftCardTransaction,
  Testimonial,
  TestimonialFormData,
  TestimonialModerationData,
  BusinessRating,
  BusinessRatingFormData,
  Appointment,
  AppointmentSeriesSummary,
  AppointmentSeriesListItem,
  AppointmentEvent,
  DepositPaymentDetails,
  InvoiceEvent,
  CreateAppointmentData,
  CreateRecurringAppointmentsData,
  RecurringPreviewResponse,
  CreateRecurringAppointmentsResult,
  OperatingHours,
  OperatingHoursFormData,
  Notification,
  ServiceReview,
  ServiceReviewFormData,
  ServiceReviewStats,
  QuoteRow,
  BlogPostDto,
  BlogPostPayload,
  BlogListResult,
  BlogListPagination,
  AskAiContactDraft,
  AskAiBookingDraft,
  AskAiCancelDraft,
  AskAiRescheduleDraft,
  AskAiReply,
  AskAiSignupDraft,
  AskAiSignupStatus,
  AskAiTurn,
} from '../types';
// BusinessRole import removed - using UserRole with permissions instead

// Determine API URL based on environment
const getApiBaseUrl = () => {
  // Check if we're in production environment
  if (process.env.REACT_APP_ENV === 'production') {
    return process.env.REACT_APP_API_URL || 'https://booking-i73o.onrender.com/api';
  }
  
  // Check if we're in QA environment
  if (process.env.REACT_APP_ENV === 'qa') {
    return process.env.REACT_APP_API_URL || 'https://booking-qa.onrender.com/api';
  }
  
  // Development environment
  return process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
};

const API_BASE_URL = getApiBaseUrl();

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        // Don't redirect for unauthenticated auth flows (expected to fail with 401)
        const requestUrl = String(error.config?.url || '');
        const isAuthChallengeAttempt =
          requestUrl.includes('/auth/login') ||
          requestUrl.includes('/auth/2fa/verify') ||
          requestUrl.includes('/auth/2fa/resend') ||
          requestUrl.includes('/auth/google-login');

        if (error.response?.status === 401 && !isAuthChallengeAttempt) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Normalize backend payload shapes into the UI's expected shape.
  // In particular, services may come back with `is_add_on` instead of `isAddOn`.
  private normalizeService(service: any): any {
    if (!service || typeof service !== 'object') return service;

    const isAddOn = service.isAddOn ?? service.is_add_on;
    const serviceLocation = service.serviceLocation ?? service.service_location;

    const coerceBool = (v: any): boolean | undefined => {
      if (v === undefined || v === null) return undefined;
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v === 1;
      if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        if (s === 'true' || s === '1' || s === 't' || s === 'yes' || s === 'y') return true;
        if (s === 'false' || s === '0' || s === 'f' || s === 'no' || s === 'n') return false;
      }
      return undefined;
    };

    const isActive = coerceBool(service.isActive ?? service.is_active);
    const deleted = coerceBool(service.deleted);
    const quotable = coerceBool(service.quotable);

    const rawDuration = service.durationMinutes ?? service.duration_minutes;
    const durationMinutes =
      rawDuration === undefined || rawDuration === null
        ? undefined
        : (Number.isFinite(Number(rawDuration)) ? Number(rawDuration) : undefined);

    const rawPrice = service.price;
    const price =
      rawPrice === undefined
        ? undefined
        : rawPrice === null
          ? null
          : typeof rawPrice === 'number'
            ? rawPrice
            : typeof rawPrice === 'string'
              ? (Number.isFinite(parseFloat(rawPrice)) ? parseFloat(rawPrice) : null)
              : null;

    const businessId = service.businessId ?? service.business_id;

    const quoteRequestInstructions =
      service.quoteRequestInstructions ?? service.quote_request_instructions;

    const category =
      service.category && typeof service.category === 'object'
        ? service.category
        : (service.category_name || service.category_icon || service.category_id)
          ? {
              id: service.category_id ?? service.categoryId ?? service.category?.id,
              name: service.category_name ?? service.categoryName ?? service.category?.name,
              icon: service.category_icon ?? service.categoryIcon ?? service.category?.icon
            }
          : undefined;

    return {
      ...service,
      ...(isAddOn !== undefined ? { isAddOn } : {}),
      ...(serviceLocation !== undefined ? { serviceLocation } : {}),
      ...(businessId !== undefined ? { businessId } : {}),
      ...(durationMinutes !== undefined ? { durationMinutes } : {}),
      ...(price !== undefined ? { price } : {}),
      ...(quotable !== undefined ? { quotable } : {}),
      ...(isActive !== undefined ? { isActive } : {}),
      ...(deleted !== undefined ? { deleted } : {}),
      ...(quoteRequestInstructions !== undefined
        ? { quoteRequestInstructions: String(quoteRequestInstructions ?? '') }
        : {}),
      ...(category !== undefined ? { category } : {})
    };
  }

  /** Normalize auth user payloads so phone is always camelCase and consistent for the UI. */
  private normalizeAuthUser(raw: any): User {
    if (!raw || typeof raw !== 'object') return raw as User;
    const rawPhone = raw.phoneNumber ?? raw.phone ?? raw.phone_number;
    const trimmed =
      rawPhone != null && String(rawPhone).trim() !== '' ? String(rawPhone).trim() : undefined;
    const next: any = { ...raw };
    if (trimmed !== undefined) {
      next.phoneNumber = trimmed;
      next.phone = trimmed;
    }
    const rawGoogle = raw.googleId ?? raw.google_id;
    const googleTrimmed =
      rawGoogle != null && String(rawGoogle).trim() !== ''
        ? String(rawGoogle).trim()
        : undefined;
    if (googleTrimmed !== undefined) {
      next.googleId = googleTrimmed;
    } else {
      delete next.googleId;
    }
    delete next.google_id;
    delete next.phone_number;
    return next as User;
  }

  // Auth endpoints
  async login(credentials: LoginCredentials): Promise<LoginApiResult> {
    const normalizedCredentials: LoginCredentials = {
      ...credentials,
      email: credentials.email.trim().toLowerCase(),
    };
    try {
      const response: AxiosResponse<ApiResponse<LoginApiResult>> = await this.api.post('/auth/login', normalizedCredentials);
      const data = response.data.data!;
      if (isLogin2faChallenge(data)) {
        return data;
      }
      return { ...data, user: this.normalizeAuthUser(data.user) };
    } catch (error) {
      throw error;
    }
  }

  async verifyLogin2fa(
    challengeId: string,
    code: string,
    rememberBrowser?: boolean
  ): Promise<AuthResponse> {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await this.api.post('/auth/2fa/verify', {
      challengeId,
      code,
      rememberBrowser: rememberBrowser === true,
    });
    const data = response.data.data!;
    return { ...data, user: this.normalizeAuthUser(data.user) };
  }

  async startBusiness2faChallenge(trustToken?: string): Promise<{
    required: boolean;
    requires2fa?: boolean;
    challengeId?: string;
    email?: string;
    expiresInSeconds?: number;
  }> {
    const response: AxiosResponse<
      ApiResponse<{
        required: boolean;
        requires2fa?: boolean;
        challengeId?: string;
        email?: string;
        expiresInSeconds?: number;
      }>
    > = await this.api.post('/auth/2fa/challenge', {
      trustToken: trustToken || undefined,
    });
    return response.data.data!;
  }

  async resendLogin2fa(challengeId: string): Promise<Login2faChallenge> {
    const response: AxiosResponse<ApiResponse<Login2faChallenge>> = await this.api.post('/auth/2fa/resend', {
      challengeId,
    });
    return response.data.data!;
  }

  async getAffiliatesForAdmin(status: 'pending' | 'approved' | 'rejected' | 'all' = 'pending'): Promise<Array<{
    id: string;
    email: string;
    firstName: string;
    lastName?: string | null;
    isEmailVerified: boolean;
    approvalStatus: 'pending' | 'approved' | 'rejected';
    programId: string;
    createdAt: string;
    codeCount: number;
  }>> {
    const response = await this.api.get('/affiliates/admin', { params: { status } });
    return response.data.data || [];
  }

  async setAffiliateApproval(id: string, status: 'approved' | 'rejected') {
    const response = await this.api.patch(`/affiliates/admin/${id}`, { status });
    return response.data.data;
  }

  async googleLogin(credential: string, loginType?: string): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<ApiResponse<AuthResponse>> = await this.api.post('/auth/google-login', {
        credential,
        loginType
      });
      const data = response.data.data!;
      return { ...data, user: this.normalizeAuthUser(data.user) };
    } catch (error) {
      throw error;
    }
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await this.api.post('/auth/register', data);
    const payload = response.data.data!;
    return { ...payload, user: this.normalizeAuthUser(payload.user) };
  }

  async registerBusiness(businessData: {
    business: {
      businessName: string;
      shortName: string;
      address: string;
      city: string;
      state: string;
      zipCode: string;
      phoneNumber: string;
      email: string;
      website?: string;
      description?: string;
      referralCode?: string;
      affiliateRef?: string;
      timezone?: string;
    };
    user: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
    };
  }): Promise<AuthResponse> {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await this.api.post('/auth/register-business', businessData);
    const payload = response.data.data!;
    return { ...payload, user: this.normalizeAuthUser(payload.user) };
  }

  /** Add a business to the current user's account (authenticated). */
  async registerBusinessForExistingUser(businessData: {
    business: {
      businessName: string;
      shortName: string;
      address: string;
      city: string;
      state: string;
      zipCode: string;
      phoneNumber: string;
      email: string;
      website?: string;
      description?: string;
      referralCode?: string;
      affiliateRef?: string;
      timezone?: string;
    };
  }): Promise<{ user: User; business: { id: string; name: string; shortName: string; address: string; phoneNumber?: string; website?: string; description?: string } }> {
    const response: AxiosResponse<ApiResponse<{ user: User; business: any }>> = await this.api.post('/auth/register-business-existing-user', businessData);
    return response.data.data!;
  }

  async validatePromoCode(code: string): Promise<{
    valid: boolean;
    status: 'EMPTY' | 'VALID' | 'EXPIRED' | 'NOT_FOUND' | 'EXHAUSTED';
    message?: string;
    trial?: {
      id: string;
      code: string;
      trialType: string;
      value: string;
      validUntil: string;
    };
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/auth/validate-promo-code', {
      params: { code },
    });
    return response.data.data!;
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  }

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.get('/auth/me');
    return this.normalizeAuthUser(response.data.data!);
  }

  async updateCurrentUser(data: Partial<Pick<User, 'firstName' | 'lastName' | 'email' | 'birthMonth' | 'birthDay'>> & { phoneNumber?: string; phone?: string }): Promise<User> {
    const sentPhoneRaw = data.phoneNumber ?? data.phone;
    const sentPhone =
      sentPhoneRaw != null && String(sentPhoneRaw).trim() !== ''
        ? String(sentPhoneRaw).trim()
        : null;

    const applyUpdate = async (path: '/auth/me' | '/users/me'): Promise<User> => {
      const response: AxiosResponse<ApiResponse<User>> = await this.api.put(path, data);
      return this.normalizeAuthUser(response.data.data!);
    };

    try {
      let updated = await applyUpdate('/auth/me');

      // Legacy /auth/me handlers may return 200 without persisting phone; retry on /users/me when needed.
      if (sentPhone !== null) {
        const returnedPhone = (updated.phoneNumber ?? updated.phone ?? '').trim();
        if (returnedPhone !== sentPhone) {
          updated = await applyUpdate('/users/me');
        }
      }

      return updated;
    } catch (err1: any) {
      if (err1?.response?.status === 404) {
        return applyUpdate('/users/me');
      }
      throw err1;
    }
  }

  async verifyEmail(token: string): Promise<{ success: boolean; message: string; accountType?: 'business' | 'customer' }> {
    const response: AxiosResponse<{ success: boolean; message: string; accountType?: 'business' | 'customer' }> = await this.api.post('/auth/verify-email', { token });
    return response.data;
  }

  async resendVerification(email: string): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.api.post('/auth/resend-verification', { email });
    return response.data;
  }

  async submitContactAACore(data: {
    businessName: string;
    contactName: string;
    phone: string;
    email: string;
    message?: string;
  }): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.api.post('/auth/contact-aacore', data);
    return response.data;
  }

  async forgotPassword(email: string, loginType?: string): Promise<void> {
    await this.api.post('/auth/forgot-password', { email: email.trim().toLowerCase(), loginType });
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await this.api.post('/auth/reset-password', { token, password });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.api.post('/auth/change-password', { currentPassword, newPassword });
    return response.data;
  }

  // Business endpoints
  async getBusinesses(options?: { includeExpired?: boolean }): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/businesses', {
      params: options?.includeExpired === undefined ? undefined : { includeExpired: options.includeExpired }
    });
    return response.data.data!;
  }

  async getUserBusinesses(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/businesses/user/businesses');
    return response.data.data!;
  }

  async markGuidedSetupShown(businessId: string): Promise<{ guidedSetupShown: boolean }> {
    const response: AxiosResponse<ApiResponse<{ guidedSetupShown: boolean }>> = await this.api.post(
      `/businesses/${businessId}/guided-setup/shown`
    );
    return response.data.data!;
  }

  async getBusiness(shortName: string, options?: { includeExpired?: boolean }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/businesses/${shortName}`, {
      params: options?.includeExpired === undefined ? undefined : { includeExpired: options.includeExpired }
    });
    return response.data.data!;
  }

  async getAskAiStatus(shortName: string): Promise<{ enabled: boolean }> {
    const response: AxiosResponse<ApiResponse<{ enabled: boolean }>> = await this.api.get(`/businesses/${shortName}/ask`);
    return response.data.data || { enabled: false };
  }

  async askBusiness(
    shortName: string,
    payload: {
      query: string;
      history?: AskAiTurn[];
      contact?: AskAiContactDraft;
      booking?: AskAiBookingDraft;
      timeZone?: string;
      pagePath?: string;
      sessionId?: string;
    }
  ): Promise<AskAiReply> {
    const response: AxiosResponse<ApiResponse<AskAiReply>> = await this.api.post(`/businesses/${shortName}/ask`, payload);
    return response.data.data!;
  }

  async flagAskAiConversation(
    shortName: string,
    payload: {
      history: AskAiTurn[];
      sessionId?: string;
      pagePath?: string;
      images: Array<{ contentType: 'image/png' | 'image/jpeg'; data: string }>;
    }
  ): Promise<{ flagged: boolean }> {
    const response: AxiosResponse<ApiResponse<{ flagged: boolean }>> = await this.api.post(
      `/businesses/${shortName}/ask/flag`,
      payload
    );
    return response.data.data || { flagged: true };
  }

  async confirmAskAiBooking(
    shortName: string,
    payload: {
      booking?: AskAiBookingDraft;
      timeZone?: string;
      pagePath?: string;
      history?: AskAiTurn[];
      sessionId?: string;
    }
  ): Promise<AskAiReply> {
    const response: AxiosResponse<ApiResponse<AskAiReply>> = await this.api.post(`/businesses/${shortName}/ask/book`, payload);
    return response.data.data!;
  }

  async getPlatformAskAiStatus(): Promise<{ enabled: boolean }> {
    const response: AxiosResponse<ApiResponse<{ enabled: boolean }>> = await this.api.get('/features/ask');
    return response.data.data || { enabled: false };
  }

  async askPlatform(payload: {
    query: string;
    history?: AskAiTurn[];
    sessionId?: string;
    signup?: AskAiSignupDraft;
  }): Promise<{
    answer: string;
    link?: [string, string];
    suggestions: string[];
    videos?: Array<{
      title: string;
      url: string;
      thumbnail: string;
      format?: string;
      videoId?: string;
    }>;
    signup?: AskAiSignupStatus;
  }> {
    const response: AxiosResponse<ApiResponse<{
      answer: string;
      link?: [string, string];
      suggestions: string[];
      videos?: Array<{
        title: string;
        url: string;
        thumbnail: string;
        format?: string;
        videoId?: string;
      }>;
      signup?: AskAiSignupStatus;
    }>> = await this.api.post('/features/ask', payload);
    return response.data.data!;
  }

  async getCustomerAskAiStatus(): Promise<{ enabled: boolean }> {
    const response: AxiosResponse<ApiResponse<{ enabled: boolean }>> = await this.api.get('/customer/ask');
    return response.data.data || { enabled: false };
  }

  async getOwnerAskAiStatus(businessId: string): Promise<{ enabled: boolean }> {
    const response: AxiosResponse<ApiResponse<{ enabled: boolean }>> = await this.api.get(
      `/businesses/${businessId}/owner-ask`
    );
    return response.data.data || { enabled: false };
  }

  async askOwner(businessId: string, payload: {
    query: string;
    history?: AskAiTurn[];
    booking?: AskAiBookingDraft;
    timeZone?: string;
    sessionId?: string;
  }): Promise<AskAiReply> {
    const response: AxiosResponse<ApiResponse<AskAiReply>> = await this.api.post(
      `/businesses/${businessId}/owner-ask`,
      payload
    );
    return response.data.data!;
  }

  async confirmOwnerAskBooking(businessId: string, payload: {
    booking?: AskAiBookingDraft;
    timeZone?: string;
    history?: AskAiTurn[];
    sessionId?: string;
  }): Promise<AskAiReply> {
    const response: AxiosResponse<ApiResponse<AskAiReply>> = await this.api.post(
      `/businesses/${businessId}/owner-ask/book`,
      payload
    );
    return response.data.data!;
  }

  async askCustomer(payload: {
    query: string;
    history?: AskAiTurn[];
    timeZone?: string;
    cancel?: AskAiCancelDraft;
    reschedule?: AskAiRescheduleDraft;
    sessionId?: string;
  }): Promise<AskAiReply> {
    const response: AxiosResponse<ApiResponse<AskAiReply>> = await this.api.post('/customer/ask', payload);
    return response.data.data!;
  }

  async getBusinessByShortName(shortName: string, options?: { includeExpired?: boolean }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/businesses/${shortName}`, {
      params: options?.includeExpired === undefined ? undefined : { includeExpired: options.includeExpired }
    });
    return response.data.data!.business;
  }

  async getBusinessPoliciesByShortName(shortName: string): Promise<{
    cancelRescheduleUpToValue: number;
    cancelRescheduleUpToUnit: 'hours' | 'days';
    cancellationPolicyText: string;
    // Included for completeness (booking flow can ignore)
    depositRequired?: boolean;
    depositAmount?: number;
    depositNonRefundableOnCancel?: boolean;
    depositDueWithinValue?: number;
    depositDueWithinUnit?: 'minutes' | 'hours' | 'days';
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/businesses/${shortName}/policies`);
    const data = response.data.data || {};
    return {
      cancelRescheduleUpToValue: Number(data.cancelRescheduleUpToValue ?? 24),
      cancelRescheduleUpToUnit: (data.cancelRescheduleUpToUnit === 'days' ? 'days' : 'hours'),
      cancellationPolicyText: String(data.cancellationPolicyText ?? ''),
      depositRequired: data.depositRequired,
      depositAmount: data.depositAmount,
      depositNonRefundableOnCancel: data.depositNonRefundableOnCancel,
      depositDueWithinValue: Number(data.depositDueWithinValue ?? DEFAULT_REGULAR_DEPOSIT_DUE_VALUE),
      depositDueWithinUnit:
        data.depositDueWithinUnit === 'days' || data.depositDueWithinUnit === 'hours'
          ? data.depositDueWithinUnit
          : 'minutes'
    };
  }

  async getCustomerAppointmentMessageByShortName(shortName: string): Promise<{ enabled: boolean; message: string }> {
    const response: AxiosResponse<ApiResponse<{ enabled: boolean; message: string }>> = await this.api.get(
      `/businesses/${shortName}/customer-appointment-message`
    );
    return {
      enabled: Boolean(response.data.data?.enabled),
      message: String(response.data.data?.message ?? '')
    };
  }

  async getSetupHealthChecklist(businessId: string): Promise<{
    timezone: boolean;
    hours: boolean;
    services: boolean;
    bizCategory: boolean;
    logo: boolean;
    images: boolean;
    theme: boolean;
  }> {
    const response: AxiosResponse<ApiResponse<{
      timezone: boolean;
      hours: boolean;
      services: boolean;
      bizCategory: boolean;
      logo: boolean;
      images: boolean;
      theme: boolean;
    }>> = await this.api.get(`/businesses/${businessId}/setup-health-checklist`);
    return response.data.data!;
  }

  async updateBusiness(businessId: string, businessData: {
    name?: string;
    shortName?: string;
    timezone?: string;
    description?: string;
    address?: string;
    phone?: string;
    email?: string;
    website?: string;
    facebookUrl?: string;
    instagramUrl?: string;
    youtubeUrl?: string;
    showContactForm?: boolean;
    websiteRedirectEnabled?: boolean;
    serviceLocation?: 'IN_STORE' | 'ON_SITE' | 'BOTH';
    categories?: string[];
    subCategories?: string[];
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/businesses/${businessId}`, businessData);
    return response.data.data!;
  }

  async getBusinessPolicies(businessId: string): Promise<{
    cancelRescheduleUpToValue: number;
    cancelRescheduleUpToUnit: 'hours' | 'days';
    depositRequired: boolean;
    depositAmount: number;
    depositNonRefundableOnCancel: boolean;
    depositDueWithinValue: number;
    depositDueWithinUnit: 'minutes' | 'hours' | 'days';
    cancellationPolicyText: string;
  }> {
    const response: AxiosResponse<ApiResponse<{
      cancelRescheduleUpToValue: number;
      cancelRescheduleUpToUnit: 'hours' | 'days';
      depositRequired: boolean;
      depositAmount: number;
      depositNonRefundableOnCancel: boolean;
      depositDueWithinValue: number;
      depositDueWithinUnit: 'minutes' | 'hours' | 'days';
      cancellationPolicyText: string;
    }>> = await this.api.get(`/businesses/${businessId}/policies`);
    return response.data.data!;
  }

  async upsertBusinessPolicies(
    businessId: string,
    policies: Partial<{
      cancelRescheduleUpToValue: number;
      cancelRescheduleUpToUnit: 'hours' | 'days';
      depositRequired: boolean;
      depositAmount: number;
      depositNonRefundableOnCancel: boolean;
      depositDueWithinValue: number;
      depositDueWithinUnit: 'minutes' | 'hours' | 'days';
      cancellationPolicyText: string;
    }>
  ): Promise<{
    cancelRescheduleUpToValue: number;
    cancelRescheduleUpToUnit: 'hours' | 'days';
    depositRequired: boolean;
    depositAmount: number;
    depositNonRefundableOnCancel: boolean;
    depositDueWithinValue: number;
    depositDueWithinUnit: 'minutes' | 'hours' | 'days';
    cancellationPolicyText: string;
  }> {
    const response: AxiosResponse<ApiResponse<{
      cancelRescheduleUpToValue: number;
      cancelRescheduleUpToUnit: 'hours' | 'days';
      depositRequired: boolean;
      depositAmount: number;
      depositNonRefundableOnCancel: boolean;
      depositDueWithinValue: number;
      depositDueWithinUnit: 'minutes' | 'hours' | 'days';
      cancellationPolicyText: string;
    }>> = await this.api.put(`/businesses/${businessId}/policies`, policies);
    return response.data.data!;
  }

  async getBusinessServices(businessId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/businesses/${businessId}/services`);
    return (response.data.data || []).map(s => this.normalizeService(s));
  }

  async getBusinessServicesByShortName(businessShortName: string): Promise<any[]> {
    const url = `/businesses/${businessShortName}/services`;
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(url);
    return (response.data.data || []).map(s => this.normalizeService(s));
  }

  async getBusinessProfessionals(businessId: string): Promise<User[]> {
    const response: AxiosResponse<ApiResponse<User[]>> = await this.api.get(`/businesses/${businessId}/professionals`);
    return response.data.data!;
  }

  async getBusinessProfessionalsByShortName(businessShortName: string): Promise<User[]> {
    const response: AxiosResponse<ApiResponse<User[]>> = await this.api.get(`/businesses/${businessShortName}/professionals`);
    return response.data.data!;
  }

  async getProfessionalDisplayPriorities(businessId: string): Promise<Array<{ professionalId: string; priority: number }>> {
    const response: AxiosResponse<ApiResponse<Array<{ professionalId: string; priority: number }>>> =
      await this.api.get(`/businesses/${businessId}/professional-display-priorities`);
    return response.data.data || [];
  }

  async getBusinessProfessionalsDetailed(businessId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/professionals/business/${businessId}`);
    return response.data.data!;
  }

  async toggleProfessionalActive(businessId: string, professionalId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.patch(`/professionals/business/${businessId}/${professionalId}/toggle`);
    return response.data.data!;
  }

  async getBusinessProfessionalsWithPriorities(businessId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/professionals/business/${businessId}/priorities`);
    return response.data.data!;
  }

  async updateProfessionalPriorities(businessId: string, priorityUpdates: Array<{ professionalId: string; priority: number }>): Promise<void> {
    await this.api.put(`/professionals/business/${businessId}/priorities`, { priorities: priorityUpdates });
  }

  async deleteProfessional(businessId: string, professionalId: string): Promise<void> {
    await this.api.delete(`/professionals/business/${businessId}/${professionalId}`);
  }

  async updatePersonnelRoles(businessId: string, personnelId: string, roleIds: string[]): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/professionals/business/${businessId}/${personnelId}/roles`, {
      roleIds
    });
    return response.data.data!;
  }

  async bulkUpdateServicePermissions(businessId: string, userIds: string[], canPerformServices: boolean): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/users/bulk/service-permissions`, {
      userIds,
      canPerformServices
    });
    return response.data.data!;
  }

  async updateUserServicePermissions(userId: string, data: { canPerformServices: boolean }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/users/${userId}/service-permissions`, data);
    return response.data.data!;
  }

  async updateUserCustomersAccess(
    userId: string,
    data: { businessId: string; canAccessCustomers: boolean }
  ): Promise<{ canAccessCustomers: boolean }> {
    const response: AxiosResponse<ApiResponse<{ canAccessCustomers: boolean }>> = await this.api.put(
      `/users/${userId}/customers-access`,
      data
    );
    return response.data.data!;
  }

  async updateUserPhoneNumberViewAccess(
    userId: string,
    data: { businessId: string; canViewFullPhoneNumber: boolean }
  ): Promise<{ canViewFullPhoneNumber: boolean }> {
    const response: AxiosResponse<ApiResponse<{ canViewFullPhoneNumber: boolean }>> = await this.api.put(
      `/users/${userId}/phone-number-view-access`,
      data
    );
    return response.data.data!;
  }

  async updateUserRoles(userId: string, data: { roleIds: string[] }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.patch(`/users/${userId}/roles`, data);
    return response.data.data!;
  }

  async getBusinessBranding(shortName: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/businesses/${shortName}/branding`);
    return response.data.data!;
  }

  async updateBusinessBranding(shortName: string, brandingData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/businesses/${shortName}/branding`, brandingData);
    return response.data.data!;
  }

  // Gallery images endpoints
  async uploadGalleryImages(shortName: string, files: File[]): Promise<{ images: Array<{ id: string; url: string; displayOrder: number }> }> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });

    const response: AxiosResponse<ApiResponse<{ images: Array<{ id: string; url: string; displayOrder: number }> }>> = 
      await this.api.post(`/businesses/${shortName}/gallery`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    return response.data.data!;
  }

  async getGalleryImages(shortName: string): Promise<Array<{ id: string; url: string; displayOrder: number }>> {
    const response: AxiosResponse<ApiResponse<Array<{ id: string; url: string; displayOrder: number }>>> = 
      await this.api.get(`/businesses/${shortName}/gallery`);
    return response.data.data!;
  }

  async getStockImages(): Promise<Array<{ key: string; url: string; name: string }>> {
    const response: AxiosResponse<ApiResponse<Array<{ key: string; url: string; name: string }>>> =
      await this.api.get('/stock-images');
    return response.data.data!;
  }

  async importStockGalleryImages(
    shortName: string,
    keys: string[]
  ): Promise<{ images: Array<{ id: string; url: string; displayOrder: number }> }> {
    const response: AxiosResponse<ApiResponse<{ images: Array<{ id: string; url: string; displayOrder: number }> }>> =
      await this.api.post(`/businesses/${shortName}/gallery/stock`, { keys });
    return response.data.data!;
  }

  async deleteGalleryImage(shortName: string, imageId: string): Promise<void> {
    await this.api.delete(`/businesses/${shortName}/gallery/${imageId}`);
  }

  async reorderGalleryImages(shortName: string, imageIds: string[]): Promise<void> {
    await this.api.put(`/businesses/${shortName}/gallery/reorder`, { imageIds });
  }

  async uploadBusinessLogo(shortName: string, logoFile: File): Promise<string> {
    const formData = new FormData();
    formData.append('logo', logoFile);
    
    const response: AxiosResponse<ApiResponse<{ logoUrl: string }>> = await this.api.post(
      `/businesses/${shortName}/upload-logo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data!.logoUrl;
  }

  // Appointment endpoints
  async getAppointments(filters?: any): Promise<{ data: Appointment[]; pagination?: any }> {
    const response: AxiosResponse<ApiResponse<Appointment[]>> = await this.api.get('/appointments', { params: filters });
    return {
      data: response.data.data!,
      pagination: response.data.pagination
    };
  }

  async getUnassignedAssignments(businessId: string): Promise<{
    count: number;
    assignments: Array<{
      assignmentId: string;
      appointmentId: string;
      serviceId: string;
      serviceName: string;
      fromDateTime: string;
      appointmentDateTime: string;
      durationMinutes: number | null;
      price: number | string | null;
      appointmentStatus: string;
      customerFirstName: string;
      customerLastName: string;
      customerName: string;
    }>;
  }> {
    const response: AxiosResponse<ApiResponse<{
      count: number;
      assignments: Array<{
        assignmentId: string;
        appointmentId: string;
        serviceId: string;
        serviceName: string;
        fromDateTime: string;
        appointmentDateTime: string;
        durationMinutes: number | null;
        price: number | string | null;
        appointmentStatus: string;
        customerFirstName: string;
        customerLastName: string;
        customerName: string;
      }>;
    }>> = await this.api.get('/appointments/unassigned', { params: { businessId } });
    return response.data.data!;
  }

  async assignProfessionalsToUnassigned(payload: {
    businessId: string;
    assignments: Array<{ assignmentId: string; professionalId: string }>;
  }): Promise<{ updatedCount: number; appointmentIds: string[] }> {
    const response: AxiosResponse<ApiResponse<{ updatedCount: number; appointmentIds: string[] }>> =
      await this.api.post('/appointments/assign-professionals', payload);
    return response.data.data!;
  }

  async getBusinessDashboardStats(params: {
    businessId: string;
    period?: string;
    year?: string;
    month?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{
    summary: {
      totalAppointments: number;
      completedAppointments: number;
      cancelledAppointments: number;
      pendingAppointments: number;
      confirmedAppointments: number;
      uniqueCustomers: number;
      totalServices: number;
      activeServices: number;
      totalProfessionals: number;
      serviceBookings: number;
    };
    appointmentsByStatus: { status: string; count: number }[];
    appointmentsOverTime: { label: string; date: string; count: number }[];
    topServices: { serviceId: string; serviceName: string; count: number }[];
    period: string;
    earliestAppointmentYear: number;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/appointments/stats', {
      params: { ...params, scope: 'business-dashboard' },
    });
    return response.data.data!;
  }

  async getAppointmentStats(params: { businessId: string; customerId: string }): Promise<{ noShows: number; lateCanceledRescheduled: number }> {
    try {
      const response: AxiosResponse<ApiResponse<{ noShows: number; lateCanceledRescheduled: number }>> = await this.api.get(
        '/appointments/stats',
        { params }
      );
      return response.data.data!;
    } catch (error: any) {
      // If the backend endpoint isn't available yet, fail gracefully.
      if (error?.response?.status === 404) {
        return { noShows: 0, lateCanceledRescheduled: 0 };
      }
      throw error;
    }
  }

  async getProfessionalAppointments(filters?: any): Promise<{ data: Appointment[]; pagination?: any }> {
    return withRetry(async () => {
      const response: AxiosResponse<ApiResponse<Appointment[]>> = await this.api.get('/appointments', { 
        params: { ...filters, professionalId: 'me' }
      });
      return {
        data: response.data.data!,
        pagination: response.data.pagination
      };
    }, { maxRetries: 2 });
  }

  async getCustomerAppointments(filters?: any): Promise<{ data: Appointment[]; pagination?: any }> {
    return withRetry(async () => {
      const response: AxiosResponse<ApiResponse<Appointment[]>> = await this.api.get('/appointments', { 
        params: { ...filters, customerId: 'me' }
      });
      return {
        data: response.data.data!,
        pagination: response.data.pagination
      };
    }, { maxRetries: 2 });
  }

  async getAppointment(id: string): Promise<Appointment> {
    return withRetry(async () => {
      const response: AxiosResponse<ApiResponse<Appointment>> = await this.api.get(`/appointments/${id}`);
      return response.data.data!;
    }, { maxRetries: 2 });
  }

  async getAppointmentEvents(appointmentId: string): Promise<AppointmentEvent[]> {
    const response: AxiosResponse<ApiResponse<AppointmentEvent[]>> = await this.api.get(`/appointments/${appointmentId}/events`);
    return response.data.data ?? [];
  }

  async createAppointment(data: CreateAppointmentData): Promise<Appointment> {
    return withRetry(async () => {
      const response: AxiosResponse<ApiResponse<Appointment>> = await this.api.post('/appointments', data);
      return response.data.data!;
    }, { maxRetries: 2 });
  }

  async previewRecurringAppointments(data: CreateRecurringAppointmentsData): Promise<RecurringPreviewResponse> {
    return withRetry(async () => {
      const response: AxiosResponse<ApiResponse<RecurringPreviewResponse>> =
        await this.api.post('/appointments/recurring/preview', data);
      return response.data.data!;
    }, { maxRetries: 2 });
  }

  async createRecurringAppointments(data: CreateRecurringAppointmentsData): Promise<CreateRecurringAppointmentsResult> {
    return withRetry(async () => {
      const response: AxiosResponse<ApiResponse<CreateRecurringAppointmentsResult>> =
        await this.api.post('/appointments/recurring', data);
      return response.data.data!;
    }, { maxRetries: 2 });
  }

  async uploadQuoteImages(businessId: string, files: File[]): Promise<{ attachmentIds: string[]; urls: string[] }> {
    const form = new FormData();
    form.append('businessId', businessId);
    files.forEach((file) => form.append('images', file));
    const response: AxiosResponse<ApiResponse<{ attachmentIds: string[]; urls: string[] }>> = await this.api.post(
      '/quotes/upload',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    const data = response.data.data!;
    return { attachmentIds: data.attachmentIds ?? [], urls: data.urls ?? [] };
  }

  /**
   * Get S3 presigned upload URLs for quote images. Call before uploading blobs directly to S3.
   */
  async getQuotePresignedUploadUrls(
    count: number,
    contentTypes?: string[]
  ): Promise<{ uploads: { uploadUrl: string; key: string; finalUrl: string }[] }> {
    const response: AxiosResponse<
      ApiResponse<{ uploads: { uploadUrl: string; key: string; finalUrl: string }[] }>
    > = await this.api.post('/quotes/presigned-upload-urls', { count, contentTypes });
    const data = response.data.data!;
    return { uploads: data.uploads ?? [] };
  }

  /**
   * Get presigned GET URLs for a quote's customer_uploaded_images (private S3). For business quote details page.
   */
  async getQuotePresignedImageUrls(quoteId: string): Promise<{ urls: string[] }> {
    const response: AxiosResponse<ApiResponse<{ urls: string[] }>> = await this.api.post(
      '/quotes/presigned-image-urls',
      { quoteId }
    );
    const data = response.data.data!;
    return { urls: data.urls ?? [] };
  }

  async createQuote(body: {
    businessId: string;
    services?: Array<{ id: string; price: number | null }>;
    serviceId?: string;
    serviceIds?: string[];
    preferredSchedule?: string;
    customerProvidedDetails?: string;
    attachmentIds?: string[];
    imageUrls?: string[];
  }): Promise<{ id: string }> {
    const response: AxiosResponse<ApiResponse<{ id: string }>> = await this.api.post('/quotes', body);
    return response.data.data!;
  }

  async getQuotes(params: {
    businessId: string;
    status?: string;
    limit?: number;
    offset?: number;
    search?: string;
    sortOrder?: 'newest' | 'oldest';
  }): Promise<{ data: QuoteRow[]; pagination?: { total: number; limit: number; offset: number } }> {
    const response: AxiosResponse<ApiResponse<QuoteRow[]>> = await this.api.get('/quotes', { params });
    return {
      data: response.data.data ?? [],
      pagination: (response.data as any).pagination
    };
  }

  async getQuotesForCustomer(customerId: string, params?: { status?: string; limit?: number }): Promise<QuoteRow[]> {
    const response: AxiosResponse<ApiResponse<QuoteRow[]>> = await this.api.get('/quotes', {
      params: { customerId, ...params }
    });
    return response.data.data ?? [];
  }

  async getQuote(quoteId: string): Promise<QuoteRow> {
    const response: AxiosResponse<ApiResponse<QuoteRow>> = await this.api.get(`/quotes/${quoteId}`);
    return response.data.data!;
  }

  async updateQuote(
    quoteId: string,
    data: {
      businessOfferAmount?: number | string | null;
      requiredDepositAmount?: number | string | null;
      offerExpirationDays?: number | string | null;
      offerExpiredAt?: string | null;
      newMessage?: { text: string };
    }
  ): Promise<{ businessOfferAmount: number | null; requiredDepositAmount: number | null; offerSentAt?: string | null; offerExpirationDays?: number | null; offerExpiredAt?: string | null; conversations: QuoteRow['conversations']; status?: string }> {
    const response: AxiosResponse<ApiResponse<{ businessOfferAmount: number | null; requiredDepositAmount: number | null; offerSentAt?: string | null; offerExpirationDays?: number | null; offerExpiredAt?: string | null; conversations: QuoteRow['conversations']; status?: string }>> = await this.api.patch(`/quotes/${quoteId}`, data);
    return response.data.data!;
  }

  async acceptQuote(quoteId: string): Promise<{ status: string; businessOfferAmount: number | null; requiredDepositAmount: number | null; offerSentAt?: string | null; offerExpirationDays?: number | null; offerExpiredAt?: string | null; conversations: QuoteRow['conversations'] }> {
    const response: AxiosResponse<ApiResponse<{ status: string; businessOfferAmount: number | null; requiredDepositAmount: number | null; offerSentAt?: string | null; offerExpirationDays?: number | null; offerExpiredAt?: string | null; conversations: QuoteRow['conversations'] }>> = await this.api.patch(`/quotes/${quoteId}`, { acceptOffer: true });
    return response.data.data!;
  }

  async createAppointmentFromQuote(
    quoteId: string,
    payload?: {
      scheduledAt?: string;
      flexScheduledAt?: { earliest: string; latest: string };
      assignments?: Array<{ serviceId: string; professionalId: string | null }>;
      /** Required when `recurring` is set; IANA timezone used for the series start (exact or window anchor). */
      clientTimeZone?: string;
      recurring?: { intervalWeeks: number; occurrenceCount: number };
      depositDeadline?: { value: number; unit: 'hours' | 'days' };
      appointmentTitle?: string;
      locationAddress?: string;
      messageToCustomer?: string;
    }
  ): Promise<{ appointmentId: string; seriesId?: string; appointmentIds?: string[] }> {
    const response: AxiosResponse<ApiResponse<{ appointmentId: string; seriesId?: string; appointmentIds?: string[] }>> =
      await this.api.post(`/quotes/${quoteId}/create-appointment`, payload || {});
    return response.data.data!;
  }

  async updateAppointment(
    id: string,
    data: Partial<CreateAppointmentData> & {
      status?: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
      noShow?: boolean;
      allowOverride?: boolean;
      sendUpdateEmail?: boolean;
    }
  ): Promise<Appointment> {
    const response: AxiosResponse<ApiResponse<Appointment>> = await this.api.put(`/appointments/${id}`, data);
    return response.data.data!;
  }

  async updateAssignmentDuration(params: {
    appointmentId: string;
    assignmentId: string;
    durationMinutes: number;
    allowOverride?: boolean;
  }): Promise<{ appointmentId: string; assignmentId: string; durationMinutes: number }> {
    const response: AxiosResponse<ApiResponse<{ appointmentId: string; assignmentId: string; durationMinutes: number }>> =
      await this.api.patch(`/appointments/${params.appointmentId}/assignments/${params.assignmentId}/duration`, {
        durationMinutes: params.durationMinutes,
        ...(params.allowOverride !== undefined ? { allowOverride: params.allowOverride } : {})
      });
    return response.data.data!;
  }

  async getAppointmentSeriesSummary(appointmentId: string): Promise<AppointmentSeriesSummary | null> {
    try {
      const response: AxiosResponse<ApiResponse<AppointmentSeriesSummary>> =
        await this.api.get(`/appointments/${appointmentId}/series-summary`);
      return response.data.data ?? null;
    } catch (error: any) {
      // Optional endpoint: fail gracefully if not yet deployed
      if (error?.response?.status === 404) return null;
      throw error;
    }
  }

  async getAppointmentSeriesAppointments(
    appointmentId: string
  ): Promise<{ appointments: AppointmentSeriesListItem[]; seriesId: string | null }> {
    const response: AxiosResponse<
      ApiResponse<{ appointments: AppointmentSeriesListItem[]; seriesId?: string }>
    > = await this.api.get(`/appointments/${appointmentId}/series-appointments`);
    const data = response.data.data;
    return {
      appointments: Array.isArray(data?.appointments) ? data!.appointments : [],
      seriesId: data?.seriesId != null ? String(data.seriesId) : null,
    };
  }

  async cancelAppointment(
    id: string,
    cancellationData: {
      noShow: boolean;
      cancellationNotes: string;
      sendCancellationEmail?: boolean;
      cancelScope?: 'THIS' | 'REMAINING';
    }
  ): Promise<void> {
    await this.api.put(`/appointments/${id}/cancel`, cancellationData);
  }

  async rescheduleAppointment(id: string, data: { scheduledAt: string }): Promise<Appointment> {
    const response: AxiosResponse<ApiResponse<Appointment>> = await this.api.put(`/appointments/${id}`, {
      scheduledAt: data.scheduledAt
    });
    return response.data.data!;
  }

  async sendAppointmentReminder(id: string): Promise<{ reminder_sent_at: string }> {
    const response: AxiosResponse<ApiResponse<{ reminder_sent_at: string }>> = await this.api.post(`/appointments/${id}/send-reminder`);
    return response.data.data!;
  }

  async markAppointmentDepositPaid(
    id: string,
    payment: {
      paymentMethod: string;
      paymentDate: string;
      referenceNumber?: string | null;
      notes?: string | null;
    }
  ): Promise<Appointment> {
    await this.api.post(`/appointments/${id}/mark-deposit-paid`, payment);
    return this.getAppointment(id);
  }

  async getAppointmentDepositPaymentDetails(id: string): Promise<DepositPaymentDetails> {
    const response: AxiosResponse<ApiResponse<DepositPaymentDetails>> = await this.api.get(
      `/appointments/${id}/deposit-payment-details`
    );
    return response.data.data!;
  }

  // Invoice endpoints
  async createInvoice(data: {
    businessId: string;
    appointmentId?: string;
    billTo: string;
    customerEmail?: string | null;
    customerPhone?: string | null;
    receiptEmail?: string | null;
    invoiceDate: string; // YYYY-MM-DD
    dueDate: string; // YYYY-MM-DD
    status?: 'DRAFT' | 'SENT' | 'PAID' | 'VOIDED';
    notes?: string | null;
    internalNotes?: string | null;
    discount?: number;
    tax?: number; // amount, not percentage
    tip?: number;
    tipNote?: string | null;
    services: any[];
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/invoices', data);
    return response.data.data!;
  }

  async getInvoice(invoiceId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/invoices/${invoiceId}`);
    return response.data.data!;
  }

  async getInvoiceEvents(invoiceId: string): Promise<InvoiceEvent[]> {
    const response: AxiosResponse<ApiResponse<InvoiceEvent[]>> = await this.api.get(
      `/invoices/${invoiceId}/events`
    );
    return response.data.data ?? [];
  }

  async getPublicInvoice(invoiceId: string, guestToken: string): Promise<any> {
    const t = String(guestToken || '').trim();
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/invoices/${invoiceId}/public`, {
      params: { t },
    });
    return response.data.data!;
  }

  async updateInvoice(invoiceId: string, data: {
    billTo?: string;
    customerEmail?: string | null;
    customerPhone?: string | null;
    receiptEmail?: string | null;
    invoiceDate?: string;
    dueDate?: string;
    status?: 'DRAFT' | 'SENT' | 'PAID' | 'VOIDED';
    notes?: string | null;
    internalNotes?: string | null;
    discount?: number;
    tax?: number;
    tip?: number;
    tipNote?: string | null;
    services?: any[];
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.patch(`/invoices/${invoiceId}`, data);
    return response.data.data!;
  }

  async sendInvoiceEmail(invoiceId: string): Promise<{ sent: boolean }> {
    const response: AxiosResponse<ApiResponse<{ sent: boolean }>> = await this.api.post(`/invoices/${invoiceId}/send`);
    return response.data.data!;
  }

  async markInvoicePaid(
    invoiceId: string,
    payment: {
      paymentMethod: string;
      paymentDate: string;
      referenceNumber?: string | null;
      notes?: string | null;
    }
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/invoices/${invoiceId}/mark-paid`, payment);
    return response.data.data!;
  }

  async downloadInvoicePdf(invoiceId: string): Promise<{ blob: Blob; filename?: string }> {
    const response = await this.api.get(`/invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
    });

    const disposition = String((response as any)?.headers?.['content-disposition'] ?? '');
    const m = disposition.match(/filename="([^"]+)"/i) || disposition.match(/filename=([^;]+)/i);
    const filename = m?.[1]?.trim();

    return { blob: response.data as Blob, filename };
  }

  async downloadPublicInvoicePdf(invoiceId: string, guestToken: string): Promise<{ blob: Blob; filename?: string }> {
    const t = String(guestToken || '').trim();
    const response = await this.api.get(`/invoices/${invoiceId}/pdf/public`, {
      params: { t },
      responseType: 'blob',
    });

    const disposition = String((response as any)?.headers?.['content-disposition'] ?? '');
    const m = disposition.match(/filename="([^"]+)"/i) || disposition.match(/filename=([^;]+)/i);
    const filename = m?.[1]?.trim();

    return { blob: response.data as Blob, filename };
  }

  async getInvoices(params: {
    businessId: string;
    customerId?: string;
    status?: 'DRAFT' | 'SENT' | 'PAID' | 'VOIDED';
    source?: 'booking' | 'manual';
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: any[]; pagination?: any }> {
    const { businessId, ...rest } = params;
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/invoices/business/${businessId}`, {
      params: rest,
    });
    return {
      data: response.data.data ?? [],
      pagination: response.data.pagination,
    };
  }

  async getMyInvoices(params: {
    status?: 'SENT' | 'PAID';
    businessId?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ data: any[]; pagination?: any }> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/invoices/my`, {
      params,
    });
    return {
      data: response.data.data ?? [],
      pagination: response.data.pagination,
    };
  }

  // Stripe endpoints
  async getStripeConfig(): Promise<{ publishableKey: string }> {
    const response: AxiosResponse<ApiResponse<{ publishableKey: string }>> = await this.api.get('/stripe/config');
    return response.data.data!;
  }

  async createInvoicePaymentIntent(invoiceId: string): Promise<{
    invoicePaymentId: string;
    paymentIntentId: string;
    clientSecret: string;
    amountCents: number;
    currency: string;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/invoices/${invoiceId}/payment-intents`);
    return response.data.data!;
  }

  async createBusinessInvoicePaymentIntent(invoiceId: string): Promise<{
    invoicePaymentId: string;
    paymentIntentId: string;
    clientSecret: string;
    amountCents: number;
    currency: string;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/invoices/${invoiceId}/payment-intents/business`);
    return response.data.data!;
  }

  async createPublicInvoicePaymentIntent(invoiceId: string, guestToken: string): Promise<{
    invoicePaymentId: string;
    paymentIntentId: string;
    clientSecret: string;
    amountCents: number;
    currency: string;
  }> {
    const t = String(guestToken || '').trim();
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/invoices/${invoiceId}/payment-intents/public`, undefined, {
      params: { t },
    });
    return response.data.data!;
  }

  async syncInvoicePaymentIntent(invoiceId: string, paymentIntentId: string): Promise<{
    paymentIntentId: string;
    status: string;
    mappedStatus: string;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/invoices/${invoiceId}/payment-intents/${paymentIntentId}/sync`);
    return response.data.data!;
  }

  async syncPublicInvoicePaymentIntent(invoiceId: string, paymentIntentId: string, guestToken: string): Promise<{
    paymentIntentId: string;
    status: string;
    mappedStatus: string;
  }> {
    const t = String(guestToken || '').trim();
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/invoices/${invoiceId}/payment-intents/${paymentIntentId}/sync/public`,
      undefined,
      { params: { t } }
    );
    return response.data.data!;
  }

  async updateInvoicePaymentIntentTip(
    invoiceId: string,
    paymentIntentId: string,
    tipCents: number
  ): Promise<{ amountCents: number; tipCents: number; currency: string; clientSecret: string; paymentIntentId: string }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.patch(
      `/invoices/${invoiceId}/payment-intents/${paymentIntentId}/tip`,
      { tipCents }
    );
    return response.data.data!;
  }

  async updatePublicInvoicePaymentIntentTip(
    invoiceId: string,
    paymentIntentId: string,
    guestToken: string,
    tipCents: number
  ): Promise<{ amountCents: number; tipCents: number; currency: string; clientSecret: string; paymentIntentId: string }> {
    const t = String(guestToken || '').trim();
    const response: AxiosResponse<ApiResponse<any>> = await this.api.patch(
      `/invoices/${invoiceId}/payment-intents/${paymentIntentId}/tip/public`,
      { tipCents },
      { params: { t } }
    );
    return response.data.data!;
  }

  // Appointment deposit payment (customer pays required_deposit_amount)
  async getAppointmentDeposit(appointmentId: string): Promise<{
    appointmentId: string;
    requestedAppointmentId?: string;
    requiredDepositAmount: number | null;
    receivedDepositAmount: number;
    amountDueCents: number;
    amountDueDollars: number;
    alreadyPaid: boolean;
    businessName: string | null;
    isSeriesDeposit?: boolean;
    seriesId?: string | null;
    seriesOccurrenceCount?: number | null;
    perOccurrenceDepositAmount?: number | null;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/appointments/${appointmentId}/deposit`);
    return response.data.data!;
  }

  async createAppointmentDepositPaymentIntent(appointmentId: string): Promise<{
    appointmentDepositPaymentId: string;
    paymentIntentId: string;
    clientSecret: string;
    amountCents: number;
    currency: string;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/appointments/${appointmentId}/deposit/payment-intents`);
    return response.data.data!;
  }

  async syncAppointmentDepositPaymentIntent(appointmentId: string, paymentIntentId: string): Promise<{
    paymentIntentId: string;
    status: string;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/appointments/${appointmentId}/deposit/payment-intents/${paymentIntentId}/sync`
    );
    return response.data.data!;
  }

  // Stripe Connect (business payouts)
  async getBusinessStripeAccount(businessId: string): Promise<any | null> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/businesses/${businessId}/stripe-account`);
    return response.data.data ?? null;
  }

  async createBusinessStripeAccount(businessId: string): Promise<{ account: any; onboardingUrl: string; expiresAt: number }>{
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/businesses/${businessId}/stripe-account`);
    return response.data.data!;
  }

  async createBusinessStripeOnboardingLink(businessId: string): Promise<{ onboardingUrl: string; expiresAt: number }>{
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/businesses/${businessId}/stripe-account/onboarding-link`);
    return response.data.data!;
  }

  async refreshBusinessStripeAccount(businessId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/businesses/${businessId}/stripe-account/refresh`);
    return response.data.data!;
  }

  async createBusinessStripeLoginLink(businessId: string): Promise<{ url: string }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/businesses/${businessId}/stripe-account/login-link`);
    return response.data.data!;
  }

  // User Role endpoints (simplified - no separate business_roles table)
  async getUserRoles(businessId: string): Promise<UserRole[]> {
    const response: AxiosResponse<ApiResponse<UserRole[]>> = await this.api.get(`/businesses/${businessId}/user-roles`);
    return response.data.data!;
  }

  async updateUserRolePermissions(businessId: string, userId: string, permissions: string[]): Promise<UserRole> {
    const response: AxiosResponse<ApiResponse<UserRole>> = await this.api.put(`/businesses/${businessId}/user-roles/${userId}`, { permissions });
    return response.data.data!;
  }

  async toggleUserRoleActive(businessId: string, roleId: string): Promise<UserRole> {
    const response: AxiosResponse<ApiResponse<UserRole>> = await this.api.put(`/businesses/${businessId}/user-roles/${roleId}/toggle-active`);
    return response.data.data!;
  }

  // Invitation endpoints
  async checkEmailExists(email: string): Promise<{
    exists: boolean;
    user?: { id: string; email: string; firstName: string; lastName: string };
    hasBusinessAccount?: boolean;
  }> {
    const response: AxiosResponse<ApiResponse<{
      exists: boolean;
      user?: { id: string; email: string; firstName: string; lastName: string };
      hasBusinessAccount?: boolean;
    }>> = await this.api.get(`/users/check-email/${encodeURIComponent(email)}`);
    return response.data.data!;
  }

  // Search users by email or name (partial match)
  async searchUsers(
    query: string,
    limit: number = 10,
    options?: { businessId?: string }
  ): Promise<Array<{ id: string; email: string; firstName: string; lastName: string; phoneNumber?: string }>> {
    const params = new URLSearchParams({
      query,
      limit: String(limit),
    });
    if (options?.businessId) {
      params.set('businessId', options.businessId);
    }
    const response: AxiosResponse<ApiResponse<Array<{ id: string; email: string; firstName: string; lastName: string; phoneNumber?: string }>>> =
      await this.api.get(`/users/search?${params.toString()}`);
    return response.data.data!;
  }

  // Customer invitation endpoints
  async createCustomerInvitation(data: {
    email?: string;
    firstName: string;
    lastName?: string;
    phone?: string;
    businessId: string;
  }): Promise<{ userId: string; email: string; emailSent: boolean; isTemporaryEmail?: boolean }> {
    const response: AxiosResponse<ApiResponse<{ userId: string; email: string; emailSent: boolean; isTemporaryEmail?: boolean }>> = 
      await this.api.post('/customers/invite', data);
    return response.data.data!;
  }

  async getCustomerSignupToken(token: string): Promise<{
    email: string;
    firstName: string;
    lastName: string;
    expiresAt: string;
  }> {
    const response: AxiosResponse<ApiResponse<{
      email: string;
      firstName: string;
      lastName: string;
      expiresAt: string;
    }>> = await this.api.get(`/customers/signup-token/${token}`);
    return response.data.data!;
  }

  async completeCustomerSignup(data: {
    token: string;
    password: string;
    acceptTerms: boolean;
  }): Promise<{ userId: string; email: string }> {
    const response: AxiosResponse<ApiResponse<{ userId: string; email: string }>> = 
      await this.api.post('/customers/complete-signup', data);
    return response.data.data!;
  }

  // Get customers for a business with pagination, search, and sorting
  async getBusinessCustomers(
    businessId: string,
    options?: {
      page?: number;
      limit?: number;
      search?: string;
      sort?: string;
    }
  ): Promise<{
    data: Array<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
      createdAt: string;
      appointmentCount: number;
      lastAppointment?: {
        id: string;
        scheduledAt: string;
        status: string;
      } | null;
      nextAppointment?: {
        id: string;
        scheduledAt: string;
        status: string;
      } | null;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.search) params.append('search', options.search);
    if (options?.sort) params.append('sort', options.sort);

    const queryString = params.toString();
    const url = `/customers/business/${businessId}${queryString ? `?${queryString}` : ''}`;

    const response: AxiosResponse<{
      success: boolean;
      data: Array<{
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        phone?: string;
        createdAt: string;
        appointmentCount: number;
        lastAppointment?: {
          id: string;
          scheduledAt: string;
          status: string;
        } | null;
        nextAppointment?: {
          id: string;
          scheduledAt: string;
          status: string;
        } | null;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
      };
    }> = await this.api.get(url);
    
    return {
      data: response.data.data,
      pagination: response.data.pagination
    };
  }

  async checkShortNameAvailability(shortName: string): Promise<{ available: boolean; message?: string }> {
    try {
      const response: AxiosResponse<ApiResponse<{ available: boolean; message?: string }>> = await this.api.get(`/businesses/check-shortname?shortName=${encodeURIComponent(shortName)}`);
      return response.data.data!;
    } catch (error: any) {
      // If the endpoint doesn't exist yet, we'll handle it gracefully
      if (error.response?.status === 404) {
        // For now, assume it's available if the endpoint doesn't exist
        return { available: true };
      }
      throw error;
    }
  }

  async createInvitation(businessId: string, invitationData: InvitationFormData): Promise<Invitation> {
    const response: AxiosResponse<ApiResponse<Invitation>> = await this.api.post(`/businesses/${businessId}/invitations`, invitationData);
    return response.data.data!;
  }

  async getInvitations(businessId: string): Promise<Invitation[]> {
    const response: AxiosResponse<ApiResponse<Invitation[]>> = await this.api.get(`/businesses/${businessId}/invitations`);
    return response.data.data!;
  }

  async resendInvitation(businessId: string, invitationId: string, inviterName?: string): Promise<void> {
    await this.api.post(`/businesses/${businessId}/invitations/${invitationId}/resend`, { inviterName });
  }

  async revokeInvitation(businessId: string, invitationId: string): Promise<void> {
    await this.api.delete(`/businesses/${businessId}/invitations/${invitationId}`);
  }

  async getInvitationByToken(token: string): Promise<Invitation> {
    const response: AxiosResponse<ApiResponse<Invitation>> = await this.api.get(`/invitations/token/${token}`);
    return response.data.data!;
  }

  async acceptInvitation(token: string, userData?: { firstName: string; lastName: string; password: string }): Promise<void> {
    await this.api.post('/invitations/accept', { token, ...userData });
  }

  // Gift Card endpoints
  async getGiftCards(businessId: string, filters?: { status?: string; search?: string }): Promise<GiftCard[]> {
    const response: AxiosResponse<ApiResponse<GiftCard[]>> = await this.api.get(`/gift-cards/business/${businessId}`, { params: filters });
    return response.data.data!;
  }

  async getUserGiftCards(filters?: { status?: string; search?: string }): Promise<GiftCard[]> {
    const response: AxiosResponse<ApiResponse<GiftCard[]>> = await this.api.get('/gift-cards/user', { params: filters });
    return response.data.data!;
  }

  async createGiftCard(businessId: string, giftCardData: GiftCardFormData): Promise<GiftCard> {
    const response: AxiosResponse<ApiResponse<GiftCard>> = await this.api.post(`/gift-cards/business/${businessId}`, giftCardData);
    return response.data.data!;
  }

  async createGiftCardIssuancePaymentIntent(
    businessId: string,
    giftCardData: GiftCardFormData
  ): Promise<{
    issuancePaymentId: string;
    paymentIntentId: string;
    clientSecret: string;
    amountCents: number;
    currency: string;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/gift-cards/business/${businessId}/issuance/payment-intents`,
      giftCardData
    );
    return response.data.data!;
  }

  async syncGiftCardIssuancePaymentIntent(
    businessId: string,
    paymentIntentId: string
  ): Promise<{
    paymentIntentId: string;
    status: string;
    mappedStatus: string;
    giftCardId: string | null;
    giftCard: GiftCard | null;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/gift-cards/business/${businessId}/issuance/payment-intents/${encodeURIComponent(paymentIntentId)}/sync`
    );
    return response.data.data!;
  }

  async createUniversalGiftCard(giftCardData: GiftCardFormData): Promise<GiftCard> {
    const response: AxiosResponse<ApiResponse<GiftCard>> = await this.api.post('/gift-cards/universal', giftCardData);
    return response.data.data!;
  }

  async createGiftCardPurchasePaymentIntent(
    businessId: string,
    giftCardData: GiftCardFormData
  ): Promise<{
    issuancePaymentId: string;
    paymentIntentId: string;
    clientSecret: string;
    amountCents: number;
    currency: string;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/gift-cards/business/${businessId}/purchase/payment-intents`,
      giftCardData
    );
    return response.data.data!;
  }

  async syncGiftCardPurchasePaymentIntent(
    businessId: string,
    paymentIntentId: string
  ): Promise<{
    paymentIntentId: string;
    status: string;
    mappedStatus: string;
    giftCardId: string | null;
    giftCard: GiftCard | null;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/gift-cards/business/${businessId}/purchase/payment-intents/${encodeURIComponent(paymentIntentId)}/sync`
    );
    return response.data.data!;
  }

  async createUniversalGiftCardPurchasePaymentIntent(giftCardData: GiftCardFormData): Promise<{
    issuancePaymentId: string;
    paymentIntentId: string;
    clientSecret: string;
    amountCents: number;
    currency: string;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      '/gift-cards/universal/purchase/payment-intents',
      giftCardData
    );
    return response.data.data!;
  }

  async syncUniversalGiftCardPurchasePaymentIntent(paymentIntentId: string): Promise<{
    paymentIntentId: string;
    status: string;
    mappedStatus: string;
    giftCardId: string | null;
    giftCard: GiftCard | null;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/gift-cards/universal/purchase/payment-intents/${encodeURIComponent(paymentIntentId)}/sync`
    );
    return response.data.data!;
  }

  async redeemGiftCardCode(code: string): Promise<GiftCard> {
    const response: AxiosResponse<ApiResponse<GiftCard>> = await this.api.post('/gift-cards/redeem-code', { code });
    return response.data.data!;
  }

  async getGiftCard(giftCardId: string): Promise<GiftCard> {
    const response: AxiosResponse<ApiResponse<GiftCard>> = await this.api.get(`/gift-cards/${giftCardId}`);
    return response.data.data!;
  }

  async updateGiftCard(giftCardId: string, giftCardData: Partial<GiftCardFormData>): Promise<GiftCard> {
    const response: AxiosResponse<ApiResponse<GiftCard>> = await this.api.patch(`/gift-cards/${giftCardId}`, giftCardData);
    return response.data.data!;
  }

  async updateGiftCardStatus(giftCardId: string, status: 'ACTIVE' | 'CANCELLED'): Promise<GiftCard> {
    const response: AxiosResponse<ApiResponse<GiftCard>> = await this.api.patch(`/gift-cards/${giftCardId}`, { status });
    return response.data.data!;
  }

  async redeemGiftCard(code: string, amount: number, appointmentId?: string, notes?: string): Promise<GiftCardTransaction> {
    const response: AxiosResponse<ApiResponse<GiftCardTransaction>> = await this.api.post('/gift-cards/redeem', { 
      code, 
      amount, 
      appointmentId, 
      notes 
    });
    return response.data.data!;
  }

  async getGiftCardTransactions(giftCardId: string): Promise<GiftCardTransaction[]> {
    const response: AxiosResponse<ApiResponse<GiftCardTransaction[]>> = await this.api.get(`/gift-cards/${giftCardId}/transactions`);
    return response.data.data!;
  }

  // Testimonial endpoints
  async getTestimonials(businessId: string, filters?: { status?: string; isPublic?: boolean; limit?: number; offset?: number }): Promise<{ data: Testimonial[]; pagination: { total: number; limit: number; offset: number } }> {
    const response: AxiosResponse<ApiResponse<{ data: Testimonial[]; pagination: { total: number; limit: number; offset: number } }>> = await this.api.get(`/testimonials/business/${businessId}`, { params: filters });
    return response.data.data!;
  }

  async getPublicTestimonials(businessId: string, limit?: number): Promise<Testimonial[]> {
    const response: AxiosResponse<ApiResponse<Testimonial[]>> = await this.api.get(`/testimonials/business/${businessId}/public`, { params: { limit } });
    return response.data.data!;
  }

  async createTestimonial(businessId: string, testimonialData: TestimonialFormData): Promise<Testimonial> {
    const response: AxiosResponse<ApiResponse<Testimonial>> = await this.api.post(`/testimonials/business/${businessId}`, testimonialData);
    return response.data.data!;
  }

  async moderateTestimonial(testimonialId: string, moderationData: TestimonialModerationData): Promise<Testimonial> {
    const response: AxiosResponse<ApiResponse<Testimonial>> = await this.api.patch(`/testimonials/${testimonialId}/moderate`, moderationData);
    return response.data.data!;
  }

  async updateTestimonialDisplay(testimonialId: string, displayData: { isPublic?: boolean; isFeatured?: boolean; displayOrder?: number }): Promise<Testimonial> {
    const response: AxiosResponse<ApiResponse<Testimonial>> = await this.api.patch(`/testimonials/${testimonialId}/display`, displayData);
    return response.data.data!;
  }

  async deleteTestimonial(testimonialId: string): Promise<void> {
    await this.api.delete(`/testimonials/${testimonialId}`);
  }

  async getTestimonialStats(businessId: string): Promise<{ total: number; pending: number; approved: number; rejected: number; public: number; featured: number; averageRating: string; totalRatings: number }> {
    const response: AxiosResponse<ApiResponse<{ total: number; pending: number; approved: number; rejected: number; public: number; featured: number; averageRating: string; totalRatings: number }>> = await this.api.get(`/testimonials/business/${businessId}/stats`);
    return response.data.data!;
  }

  // Category endpoints
  async getServiceCategories(businessId: string): Promise<{ id: string; name: string; description?: string; icon?: string; display_order?: number; background_color?: string | null }[]> {
    const params = { businessId };
    const response: AxiosResponse<{ id: string; name: string; description?: string; icon?: string; display_order?: number; background_color?: string | null }[]> = await this.api.get('/categories', { params });
    return response.data;
  }

  async createServiceCategory(categoryData: { name: string; description?: string; icon?: string; display_order?: number; background_color?: string | null; businessId?: string }): Promise<{ id: string; name: string; description?: string; icon?: string; display_order?: number; background_color?: string | null; is_active?: boolean }> {
    const response: AxiosResponse<{ id: string; name: string; description?: string; icon?: string; display_order?: number; background_color?: string | null; is_active?: boolean }> = await this.api.post('/categories', categoryData);
    return response.data;
  }

  async updateServiceCategory(categoryId: string, categoryData: { name: string; description?: string; icon?: string; display_order?: number; background_color?: string | null; is_active?: boolean }): Promise<{ id: string; name: string; description?: string; icon?: string; display_order?: number; background_color?: string | null; is_active?: boolean }> {
    const response: AxiosResponse<{ id: string; name: string; description?: string; icon?: string; display_order?: number; background_color?: string | null; is_active?: boolean }> = await this.api.put(`/categories/${categoryId}`, categoryData);
    return response.data;
  }

  async deleteServiceCategory(categoryId: string, businessId?: string): Promise<void> {
    await this.api.delete(`/categories/${categoryId}`, { params: businessId ? { businessId } : undefined });
  }

  // Service endpoints
  async getServices(businessId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/businesses/${businessId}/services`);
    return (response.data.data || []).map(s => this.normalizeService(s));
  }

  async getAllServices(businessId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/businesses/${businessId}/services/all`);
    return (response.data.data || []).map(s => this.normalizeService(s));
  }

  async getService(serviceId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/services/${serviceId}`);
    return this.normalizeService(response.data.data!);
  }

  async createService(serviceData: {
    businessId: string;
    name: string;
    description?: string;
    durationMinutes: number;
    price: number | null;
    quotable?: boolean;
    quoteRequestInstructions?: string;
    categoryId?: string;
    icon?: string;
    isActive?: boolean;
    isAddOn?: boolean;
    serviceLocation?: 'IN_STORE' | 'ON_SITE';
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/services', serviceData);
    return this.normalizeService(response.data.data!);
  }

  async updateService(serviceId: string, serviceData: {
    name?: string;
    description?: string;
    durationMinutes?: number;
    price?: number | null;
    quotable?: boolean;
    quoteRequestInstructions?: string;
    categoryId?: string;
    icon?: string | null;
    isActive?: boolean;
    isAddOn?: boolean;
    serviceLocation?: 'IN_STORE' | 'ON_SITE';
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/services/${serviceId}`, serviceData);
    return this.normalizeService(response.data.data!);
  }

  async deleteService(serviceId: string): Promise<void> {
    await this.api.delete(`/services/${serviceId}`);
  }

  async softDeleteService(serviceId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.patch(`/services/${serviceId}/soft-delete`);
    return this.normalizeService(response.data.data!);
  }

  async toggleServiceActive(serviceId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.patch(`/services/${serviceId}/toggle-active`);
    return this.normalizeService(response.data.data!);
  }

  // Favorite Services endpoints
  async getFavoriteServices(audience?: 'customer' | 'professional', businessId?: string): Promise<any[]> {
    const params: any = {};
    if (audience) params.audience = audience;
    if (businessId) params.businessId = businessId;
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/favorite-services', { params });
    return response.data.data!;
  }

  async checkFavoriteStatus(serviceId: string, audience?: 'customer' | 'professional', businessId?: string): Promise<{ isFavorited: boolean; favoriteId: string | null }> {
    const params: any = {};
    if (audience) params.audience = audience;
    if (businessId) params.businessId = businessId;
    const response: AxiosResponse<ApiResponse<{ isFavorited: boolean; favoriteId: string | null }>> = await this.api.get(`/favorite-services/check/${serviceId}`, { params });
    return response.data.data!;
  }

  async addFavoriteService(serviceId: string, audience: 'customer' | 'professional' = 'customer', businessId?: string): Promise<any> {
    const body: any = { serviceId, audience };
    if (businessId) body.businessId = businessId;
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/favorite-services', body);
    return response.data.data!;
  }

  async removeFavoriteService(serviceId: string, audience?: 'customer' | 'professional', businessId?: string): Promise<void> {
    const params: any = {};
    if (audience) params.audience = audience;
    if (businessId) params.businessId = businessId;
    await this.api.delete(`/favorite-services/${serviceId}`, { params });
  }

  async toggleFavoriteService(serviceId: string, audience: 'customer' | 'professional' = 'customer', businessId?: string): Promise<{ isFavorited: boolean }> {
    const body: any = { audience };
    if (businessId) body.businessId = businessId;
    const response: AxiosResponse<ApiResponse<{ isFavorited: boolean }>> = await this.api.post(`/favorite-services/toggle/${serviceId}`, body);
    return response.data.data!;
  }

  // Personnel Schedule endpoints
  async getProfessionalSchedule(businessId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/professional-schedules/business/${businessId}`);
    return response.data.data!;
  }

  // Owner/admin view: get schedules for a specific professional in a business
  async getProfessionalSchedulesForProfessional(professionalId: string, businessId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/professional-schedules/${professionalId}/${businessId}`);
    return response.data.data!;
  }

  // Owner/admin: create schedules for a professional (used when a day has no schedule yet)
  async createProfessionalSchedules(
    professionalId: string,
    businessId: string,
    schedules: Array<{
      dayOfWeek: number;
      startTime?: string;
      endTime?: string;
      isAvailable?: boolean;
      isBreakTime?: boolean;
      notes?: string;
    }>
  ): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.post(`/professional-schedules`, {
      professionalId,
      businessId,
      schedules
    });
    return response.data.data!;
  }

  async updateProfessionalScheduleById(
    scheduleId: string,
    scheduleData: {
      dayOfWeek?: number;
      startTime?: string;
      endTime?: string;
      isAvailable?: boolean;
      isBreakTime?: boolean;
      notes?: string;
      businessId?: string;
    }
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/professional-schedules/${scheduleId}`, scheduleData);
    return response.data.data!;
  }

  async deleteProfessionalScheduleById(scheduleId: string, businessId?: string): Promise<void> {
    await this.api.delete(`/professional-schedules/${scheduleId}`, { data: businessId ? { businessId } : undefined });
  }

  // Owner override: bulk upsert schedules for a specific professional (no conflict validation)
  async ownerBulkUpsertProfessionalSchedules(
    businessId: string,
    professionalId: string,
    schedules: Array<{
      dayOfWeek: number;
      startTime?: string;
      endTime?: string;
      isAvailable?: boolean;
      isBreakTime?: boolean;
      notes?: string;
    }>
  ): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.put(
      `/professional-schedules/business/${businessId}/professional/${professionalId}/bulk-override`,
      { schedules }
    );
    return response.data.data!;
  }

  // Owner override: delete schedule for a professional/day (no conflict validation)
  async ownerDeleteProfessionalScheduleDay(businessId: string, professionalId: string, dayOfWeek: number): Promise<void> {
    await this.api.delete(
      `/professional-schedules/business/${businessId}/professional/${professionalId}/day/${dayOfWeek}/override`
    );
  }



  async createProfessionalTimeOff(businessId: string, professionalId: string, timeOffData: {
    label: string;
    startDate: string;
    endDate: string;
    isAllDay: boolean;
    startTime?: string;
    endTime?: string;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/professional-timeoffs', {
      businessId,
      professionalId,
      ...timeOffData
    });
    return response.data.data!;
  }

  async getProfessionalTimeOffs(businessId: string, professionalId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/professional-timeoffs`, {
      params: { businessId, professionalId }
    });
    return response.data.data!;
  }

  async updateProfessionalTimeOff(timeOffId: string, timeOffData: {
    label: string;
    startDate: string;
    endDate: string;
    isAllDay: boolean;
    startTime?: string;
    endTime?: string;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/professional-timeoffs/${timeOffId}`, timeOffData);
    return response.data.data!;
  }

  async deleteProfessionalTimeOff(timeOffId: string): Promise<void> {
    await this.api.delete(`/professional-timeoffs/${timeOffId}`);
  }

  // Owner override: professional time off management for selected personnel (owner-only)
  async ownerGetProfessionalTimeOffs(businessId: string, professionalId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(
      `/professional-timeoffs/business/${businessId}/professional/${professionalId}/override`
    );
    return response.data.data!;
  }

  async ownerCreateProfessionalTimeOff(
    businessId: string,
    professionalId: string,
    timeOffData: {
      label: string;
      startDate: string;
      endDate: string;
      isAllDay: boolean;
      startTime?: string;
      endTime?: string;
    }
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/professional-timeoffs/business/${businessId}/professional/${professionalId}/override`,
      timeOffData
    );
    return response.data.data!;
  }

  async ownerUpdateProfessionalTimeOff(
    businessId: string,
    professionalId: string,
    timeOffId: string,
    timeOffData: {
      label: string;
      startDate: string;
      endDate: string;
      isAllDay: boolean;
      startTime?: string;
      endTime?: string;
    }
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
      `/professional-timeoffs/business/${businessId}/professional/${professionalId}/${timeOffId}/override`,
      timeOffData
    );
    return response.data.data!;
  }

  async ownerDeleteProfessionalTimeOff(businessId: string, professionalId: string, timeOffId: string): Promise<void> {
    await this.api.delete(`/professional-timeoffs/business/${businessId}/professional/${professionalId}/${timeOffId}/override`);
  }

  async saveProfessionalSchedule(businessId: string, scheduleData: {
    dayOfWeek: number;
    startTime?: string;
    endTime?: string;
    isAvailable?: boolean;
    isBreakTime?: boolean;
    notes?: string;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/professional-schedules/business/${businessId}`, scheduleData);
    return response.data.data!;
  }

  async bulkUpdateProfessionalSchedules(businessId: string, schedules: Array<{
    dayOfWeek: number;
    startTime?: string;
    endTime?: string;
    isAvailable?: boolean;
    isBreakTime?: boolean;
    notes?: string;
  }>): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.put(`/professional-schedules/business/${businessId}/bulk`, { schedules });
    return response.data.data!;
  }

  async deleteProfessionalSchedule(businessId: string, dayOfWeek: number): Promise<void> {
    await this.api.delete(`/professional-schedules/business/${businessId}/day/${dayOfWeek}`);
  }

  async getAvailableProfessionals(businessId: string, serviceId: string, date: string, time: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/professionals/service/${serviceId}/available`, {
      params: { businessId, date, time }
    });
    return response.data.data!;
  }

  async getProfessionalAvailability(businessId: string, professionalId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/professional-schedules/business/${businessId}/professional/${professionalId}/availability`);
    return response.data.data!;
  }

  async getMultiProfessionalAvailability(
    businessId: string,
    professionalIds: string[],
    date?: string,
    serviceDuration?: number,
    clientTimeZone?: string
  ): Promise<any> {
    return withRetry(async () => {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/professional-schedules/business/${businessId}/availability`, {
        professionalIds,
        date,
        serviceDuration,
        clientTimeZone
      });
      return response.data.data!;
    }, { maxRetries: 3 });
  }

  async getSequentialMultiServiceAvailability(
    businessId: string,
    date: string,
    assignments: Array<{ professionalId: string; durationMinutes: number }>,
    clientTimeZone?: string
  ): Promise<any> {
    return withRetry(async () => {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
        `/professional-schedules/business/${businessId}/sequential-availability`,
        { date, assignments, clientTimeZone }
      );
      return response.data.data!;
    }, { maxRetries: 3 });
  }

  // Personnel Services Management
  async getProfessionalServices(businessId: string, professionalId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/businesses/${businessId}/professionals/${professionalId}/services`);
    return response.data.data!;
  }

  async assignServiceToProfessional(businessId: string, professionalId: string, serviceId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/businesses/${businessId}/professionals/${professionalId}/services`, {
      serviceId
    });
    return response.data.data!;
  }

  async unassignServiceFromProfessional(businessId: string, professionalId: string, serviceId: string): Promise<void> {
    await this.api.delete(`/businesses/${businessId}/professionals/${professionalId}/services/${serviceId}`);
  }

  // Operating Hours endpoints
  async getOperatingHours(businessId: string): Promise<OperatingHours[]> {
    const response: AxiosResponse<ApiResponse<OperatingHours[]>> = await this.api.get(`/businesses/${businessId}/operating-hours`);
    return response.data.data!;
  }

  async updateOperatingHours(businessId: string, operatingHours: OperatingHoursFormData[]): Promise<void> {
    await this.api.put(`/businesses/${businessId}/operating-hours`, { operatingHours });
  }

  // Business Closures endpoints
  async getBusinessClosures(businessId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/businesses/${businessId}/closures`);
    return response.data.data!;
  }

  async createBusinessClosure(businessId: string, closureData: {
    startDate: string;
    endDate: string;
    closureType: 'all_day' | 'time_range';
    startTime?: string;
    endTime?: string;
    reason?: string;
    repeatYearly?: boolean;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/businesses/${businessId}/closures`, closureData);
    return response.data.data!;
  }

  async updateBusinessClosure(businessId: string, closureId: string, closureData: {
    startDate: string;
    endDate: string;
    closureType: 'all_day' | 'time_range';
    startTime?: string;
    endTime?: string;
    reason?: string;
    repeatYearly?: boolean;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/businesses/${businessId}/closures/${closureId}`, closureData);
    return response.data.data!;
  }

  async deleteBusinessClosure(businessId: string, closureId: string): Promise<void> {
    await this.api.delete(`/businesses/${businessId}/closures/${closureId}`);
  }

  async getBusinessClosuresForDateRange(businessId: string, startDate: string, endDate: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/businesses/${businessId}/closures/check?startDate=${startDate}&endDate=${endDate}`);
    return response.data.data!;
  }

  // Role management endpoints
  async getBusinessRoles(businessId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/roles/business/${businessId}`);
    return response.data.data!;
  }

  async getActiveBusinessRoles(businessId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/roles/business/${businessId}/active`);
    return response.data.data!;
  }

  async createRole(businessId: string, roleData: {
    name: string;
    description: string;
    permissions: string[];
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/roles/business/${businessId}`, roleData);
    return response.data.data!;
  }

  async updateRole(businessId: string, roleId: string, roleData: {
    name: string;
    description: string;
    permissions: string[];
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/roles/business/${businessId}/${roleId}`, roleData);
    return response.data.data!;
  }

  async toggleBusinessRoleActive(businessId: string, roleId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/roles/business/${businessId}/${roleId}/toggle-active`);
    return response.data.data!;
  }

  async deleteBusinessRole(businessId: string, roleId: string): Promise<void> {
    await this.api.delete(`/roles/business/${businessId}/${roleId}`);
  }

  async getPermissions(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/roles/permissions');
    return response.data.data!;
  }

  // Notification management endpoints
  async getNotifications(businessId?: string, options?: { page?: number; limit?: number; isRead?: boolean; category?: 'APPOINTMENT' | 'REVIEW' | 'SERVICE' | 'ALERT' }): Promise<{ data: Notification[]; pagination?: { total: number; page: number; limit: number; totalPages: number; hasNextPage: boolean; hasPrevPage: boolean } }> {
    const url = businessId ? `/notifications/business/${businessId}` : '/notifications/business';
    const params: any = {};
    if (options?.page) params.page = options.page;
    if (options?.limit) params.limit = options.limit;
    if (options?.isRead !== undefined) params.isRead = options.isRead;
    if (options?.category) params.category = options.category;
    
    const response: AxiosResponse<ApiResponse<Notification[]>> = await this.api.get(url, { params });
    return {
      data: response.data.data!,
      pagination: (response.data as any).pagination
    };
  }

  async markNotificationAsRead(notificationId: string): Promise<Notification> {
    const response: AxiosResponse<ApiResponse<Notification>> = await this.api.put(`/notifications/${notificationId}/mark-read`);
    return response.data.data!;
  }

  async markAllNotificationsAsRead(businessId?: string): Promise<{ updatedCount: number }> {
    const url = businessId ? `/notifications/business/${businessId}/mark-all-read` : '/notifications/business/mark-all-read';
    const response: AxiosResponse<ApiResponse<{ updatedCount: number }>> = await this.api.put(url);
    return response.data.data!;
  }

  // Service Review endpoints
  async getServiceReviews(serviceId: string, filters?: { isPublic?: boolean; limit?: number; offset?: number }): Promise<{ data: ServiceReview[]; pagination: { total: number; limit: number; offset: number } }> {
    const response: AxiosResponse<{ success: boolean; data: ServiceReview[]; pagination: { total: number; limit: number; offset: number } }> = await this.api.get(`/service-reviews/service/${serviceId}`, { params: filters });
    return {
      data: response.data.data,
      pagination: response.data.pagination
    };
  }

  async getProfessionalReviews(professionalId: string, filters?: { isPublic?: boolean; limit?: number; offset?: number }): Promise<{ data: ServiceReview[]; pagination: { total: number; limit: number; offset: number } }> {
    const response: AxiosResponse<{ success: boolean; data: ServiceReview[]; pagination: { total: number; limit: number; offset: number } }> = await this.api.get(`/service-reviews/professional/${professionalId}`, { params: filters });
    return {
      data: response.data.data,
      pagination: response.data.pagination
    };
  }

  async addReviewReply(reviewId: string, message: string, parentReplyId?: string): Promise<ServiceReview> {
    const response: AxiosResponse<ApiResponse<ServiceReview>> = await this.api.post(`/service-reviews/${reviewId}/replies`, {
      message,
      parentReplyId
    });
    return response.data.data!;
  }

  async updateReviewReply(reviewId: string, replyId: string, message: string): Promise<ServiceReview> {
    const response: AxiosResponse<ApiResponse<ServiceReview>> = await this.api.put(`/service-reviews/${reviewId}/replies/${replyId}`, {
      message
    });
    return response.data.data!;
  }

  async getAppointmentReviews(appointmentId: string): Promise<ServiceReview[]> {
    const response: AxiosResponse<ApiResponse<ServiceReview[]>> = await this.api.get(`/service-reviews/appointment/${appointmentId}`);
    return response.data.data!;
  }

  async createServiceReview(reviewData: ServiceReviewFormData): Promise<ServiceReview> {
    const response: AxiosResponse<ApiResponse<ServiceReview>> = await this.api.post('/service-reviews', reviewData);
    return response.data.data!;
  }

  async updateServiceReview(reviewId: string, reviewData: Partial<ServiceReviewFormData>): Promise<ServiceReview> {
    const response: AxiosResponse<ApiResponse<ServiceReview>> = await this.api.put(`/service-reviews/${reviewId}`, reviewData);
    return response.data.data!;
  }

  async deleteServiceReview(reviewId: string): Promise<void> {
    await this.api.delete(`/service-reviews/${reviewId}`);
  }

  async updateServiceReviewVisibility(reviewId: string, isPublic: boolean, isFeatured: boolean): Promise<ServiceReview> {
    const response: AxiosResponse<ApiResponse<ServiceReview>> = await this.api.patch(`/service-reviews/${reviewId}/visibility`, {
      isPublic,
      isFeatured
    });
    return response.data.data!;
  }

  async getServiceReviewStats(serviceId: string): Promise<ServiceReviewStats> {
    const response: AxiosResponse<ApiResponse<ServiceReviewStats>> = await this.api.get(`/service-reviews/service/${serviceId}/stats`);
    return response.data.data!;
  }

  async getProfessionalReviewStats(professionalId: string): Promise<ServiceReviewStats> {
    const response: AxiosResponse<ApiResponse<ServiceReviewStats>> = await this.api.get(`/service-reviews/professional/${professionalId}/stats`);
    return response.data.data!;
  }

  async getBusinessServiceReviews(businessId: string, limit?: number): Promise<ServiceReview[]> {
    const response: AxiosResponse<ApiResponse<ServiceReview[]>> = await this.api.get(`/service-reviews/business/${businessId}`, { params: { limit } });
    return response.data.data!;
  }

  async getBusinessServiceReviewsManage(
    businessId: string,
    params?: { isPublic?: boolean; search?: string; limit?: number; offset?: number }
  ): Promise<{
    data: ServiceReview[];
    pagination: { total: number; limit: number; offset: number };
    stats: { total: number; public: number; featured: number; averageRating: number };
  }> {
    const response: AxiosResponse<{
      success: boolean;
      data: ServiceReview[];
      pagination: { total: number; limit: number; offset: number };
      stats: { total: number; public: number; featured: number; averageRating: number };
    }> = await this.api.get(`/service-reviews/business/${businessId}/manage`, { params });
    return {
      data: response.data.data,
      pagination: response.data.pagination,
      stats: response.data.stats
    };
  }

  // Business Rating endpoints (overall appointment/business experience)
  async getBusinessRatingForAppointment(appointmentId: string): Promise<BusinessRating | null> {
    const response: AxiosResponse<ApiResponse<BusinessRating | null>> = await this.api.get(`/business-ratings/appointment/${appointmentId}`);
    return response.data.data ?? null;
  }

  async createBusinessRating(data: BusinessRatingFormData): Promise<BusinessRating> {
    const response: AxiosResponse<ApiResponse<BusinessRating>> = await this.api.post('/business-ratings', data);
    return response.data.data!;
  }

  async getBusinessRatingsManage(
    businessId: string,
    params?: { isPublic?: boolean; search?: string; limit?: number; offset?: number }
  ): Promise<{
    data: BusinessRating[];
    pagination: { total: number; limit: number; offset: number };
    stats: { total: number; public: number; featured: number; averageRating: number };
  }> {
    const response: AxiosResponse<{
      success: boolean;
      data: BusinessRating[];
      pagination: { total: number; limit: number; offset: number };
      stats: { total: number; public: number; featured: number; averageRating: number };
    }> = await this.api.get(`/business-ratings/business/${businessId}/manage`, { params });
    return {
      data: response.data.data,
      pagination: response.data.pagination,
      stats: response.data.stats,
    };
  }

  async updateBusinessRatingVisibility(ratingId: string, isPublic: boolean): Promise<BusinessRating> {
    const response: AxiosResponse<ApiResponse<BusinessRating>> = await this.api.patch(`/business-ratings/${ratingId}/visibility`, {
      isPublic,
    });
    return response.data.data!;
  }

  async addBusinessRatingReply(ratingId: string, message: string): Promise<BusinessRating> {
    const response: AxiosResponse<ApiResponse<BusinessRating>> = await this.api.post(`/business-ratings/${ratingId}/replies`, {
      message,
    });
    return response.data.data!;
  }

  async updateBusinessRatingReply(ratingId: string, replyId: string, message: string): Promise<BusinessRating> {
    const response: AxiosResponse<ApiResponse<BusinessRating>> = await this.api.put(`/business-ratings/${ratingId}/replies/${replyId}`, {
      message,
    });
    return response.data.data!;
  }

  // Business Settings endpoints
  async getBusinessSettings(businessId: string): Promise<{
    autoConfirmAppointments: boolean;
    emailAppointmentActivitiesToOwner?: boolean;
    customerAppointmentMessageEnabled?: boolean;
    customerAppointmentMessage?: string;
    showProfessionalNameOnAssignment?: boolean;
  }> {
    const response: AxiosResponse<ApiResponse<{
      autoConfirmAppointments: boolean;
      emailAppointmentActivitiesToOwner?: boolean;
      customerAppointmentMessageEnabled?: boolean;
      customerAppointmentMessage?: string;
      showProfessionalNameOnAssignment?: boolean;
    }>> = await this.api.get(`/businesses/${businessId}/settings`);
    return response.data.data!;
  }

  async updateBusinessSettings(
    businessId: string,
    settings: Partial<{
      autoConfirmAppointments: boolean;
      emailAppointmentActivitiesToOwner?: boolean;
      customerAppointmentMessageEnabled?: boolean;
      customerAppointmentMessage?: string;
      showProfessionalNameOnAssignment?: boolean;
    }>
  ): Promise<{
    autoConfirmAppointments: boolean;
    emailAppointmentActivitiesToOwner?: boolean;
    customerAppointmentMessageEnabled?: boolean;
    customerAppointmentMessage?: string;
    showProfessionalNameOnAssignment?: boolean;
  }> {
    const response: AxiosResponse<ApiResponse<{
      autoConfirmAppointments: boolean;
      emailAppointmentActivitiesToOwner?: boolean;
      customerAppointmentMessageEnabled?: boolean;
      customerAppointmentMessage?: string;
      showProfessionalNameOnAssignment?: boolean;
    }>> = await this.api.put(`/businesses/${businessId}/settings`, settings);
    return response.data.data!;
  }

  async getOfferSettings(businessId: string): Promise<{
    requireDepositByDefault: boolean;
    defaultDepositAmount: number;
    notifyOwnerOnQuoteChatMessage: boolean;
  }> {
    const response: AxiosResponse<ApiResponse<{ requireDepositByDefault: boolean; defaultDepositAmount: number; notifyOwnerOnQuoteChatMessage: boolean }>> =
      await this.api.get(`/businesses/${businessId}/offer-settings`);
    return response.data.data!;
  }

  async updateOfferSettings(
    businessId: string,
    settings: Partial<{
      requireDepositByDefault: boolean;
      defaultDepositAmount: number;
      notifyOwnerOnQuoteChatMessage: boolean;
    }>
  ): Promise<{
    requireDepositByDefault: boolean;
    defaultDepositAmount: number;
    notifyOwnerOnQuoteChatMessage: boolean;
  }> {
    const response: AxiosResponse<
      ApiResponse<{ requireDepositByDefault: boolean; defaultDepositAmount: number; notifyOwnerOnQuoteChatMessage: boolean }>
    > = await this.api.put(`/businesses/${businessId}/offer-settings`, settings);
    return response.data.data!;
  }

  /** Business UUID (authenticated) or public short name — same path; server resolves by shape. */
  async getAppointmentBookingSettings(businessIdOrShortName: string): Promise<{
    bookingSlotMinutes: 15 | 30 | 60;
    useOneHourBookingBlocks: boolean;
  }> {
    const response: AxiosResponse<ApiResponse<{ bookingSlotMinutes: 15 | 30 | 60; useOneHourBookingBlocks: boolean }>> =
      await this.api.get(`/businesses/${businessIdOrShortName}/appointment-booking-settings`);
    return response.data.data!;
  }

  async updateAppointmentBookingSettings(
    businessId: string,
    settings: { bookingSlotMinutes: 15 | 30 | 60 }
  ): Promise<{ bookingSlotMinutes: 15 | 30 | 60; useOneHourBookingBlocks: boolean }> {
    const response: AxiosResponse<ApiResponse<{ bookingSlotMinutes: 15 | 30 | 60; useOneHourBookingBlocks: boolean }>> =
      await this.api.put(`/businesses/${businessId}/appointment-booking-settings`, settings);
    return response.data.data!;
  }

  // Private Professional Notes endpoints
  async getCustomerPrivateNotes(customerId: string, businessId: string): Promise<{ notes: string; createdAt?: string; updatedAt?: string }> {
    const response: AxiosResponse<ApiResponse<{ notes: string; createdAt?: string; updatedAt?: string }>> = await this.api.get(`/customers/${customerId}/notes/${businessId}`);
    return response.data.data!;
  }

  async updateCustomerPrivateNotes(customerId: string, businessId: string, notes: string): Promise<{ notes: string; updatedAt: string }> {
    const response: AxiosResponse<ApiResponse<{ notes: string; updatedAt: string }>> = await this.api.put(`/customers/${customerId}/notes/${businessId}`, { notes });
    return response.data.data!;
  }

  async updateCustomerProfile(customerId: string, businessId: string, data: { firstName?: string; lastName?: string; phone?: string }): Promise<{ id: string; firstName: string; lastName: string; email: string; phone: string | null }> {
    const response: AxiosResponse<ApiResponse<{ id: string; firstName: string; lastName: string; email: string; phone: string | null }>> = await this.api.put(`/customers/${customerId}/profile/${businessId}`, data);
    return response.data.data!;
  }

  // Contact form submission
  async submitContactForm(businessId: string, data: { name: string; email: string; phone?: string; message: string; recaptchaToken: string }): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<ApiResponse<{ success: boolean; message: string }>> = await this.api.post(`/businesses/${businessId}/contact`, data);
    return response.data.data!;
  }

  async submitSupportRequest(
    businessId: string,
    data: FormData | { name?: string; email: string; topic?: string; steps?: string; device?: string; businessName?: string }
  ): Promise<{ sent: boolean }> {
    const response: AxiosResponse<ApiResponse<{ sent: boolean }>> = await this.api.post(
      `/businesses/${businessId}/support`,
      data,
      data instanceof FormData
        ? {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        : undefined
    );
    return response.data.data!;
  }

  // Business Categories endpoints
  async getBusinessCategories(): Promise<Array<{ id: string; name: string; description?: string; created_at?: string; updated_at?: string; subcategories?: Array<{ id: string; name: string; description?: string; created_at?: string; updated_at?: string }> }>> {
    const response: AxiosResponse<ApiResponse<Array<{ id: string; name: string; description?: string; created_at?: string; updated_at?: string; subcategories?: Array<{ id: string; name: string; description?: string; created_at?: string; updated_at?: string }> }>>> = await this.api.get('/business-categories');
    return response.data.data!;
  }

  async getBusinessSubcategories(categoryId: string): Promise<Array<{ id: string; name: string; description?: string; created_at?: string; updated_at?: string }>> {
    const response: AxiosResponse<ApiResponse<Array<{ id: string; name: string; description?: string; created_at?: string; updated_at?: string }>>> = await this.api.get(`/business-categories/${categoryId}/subcategories`);
    return response.data.data!;
  }

  /** Returns the currently active scheduled maintenance window, or null if none. Public endpoint; no auth required. */
  async getMaintenanceStatus(): Promise<{
    id: string;
    message: string;
    from: string;
    to: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  } | null> {
    const response: AxiosResponse<ApiResponse<{
      id: string;
      message: string;
      from: string;
      to: string;
      status: string;
      createdAt: string;
      updatedAt: string;
    } | null>> = await this.api.get('/maintenance/check');
    return response.data.data ?? null;
  }

  /** Returns upcoming SCHEDULED maintenance within 1 day (for awareness banners), or null. Public endpoint. */
  async getUpcomingMaintenance(): Promise<{
    id: string;
    message: string;
    from: string;
    to: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  } | null> {
    const response: AxiosResponse<ApiResponse<{
      id: string;
      message: string;
      from: string;
      to: string;
      status: string;
      createdAt: string;
      updatedAt: string;
    } | null>> = await this.api.get('/maintenance/upcoming');
    return response.data.data ?? null;
  }

  /** Public blog listing (published + public). Server-side filter, sort, pagination (default 25/page). */
  async getBlogPosts(params?: {
    category?: string;
    q?: string;
    sort?: string;
    page?: number;
    limit?: number;
  }): Promise<BlogListResult> {
    const response: AxiosResponse<
      ApiResponse<BlogPostDto[]> & {
        pagination?: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
          hasNextPage: boolean;
          hasPrevPage: boolean;
        };
        meta?: { categoryCounts: Record<string, number>; featured: BlogPostDto | null };
      }
    > = await this.api.get('/blogs', {
      params: {
        ...params,
        limit: params?.limit ?? 25,
        page: params?.page ?? 1,
      },
    });
    const body = response.data;
    const p = body.pagination;
    const limit = p?.limit ?? 25;
    return {
      posts: body.data ?? [],
      pagination: {
        total: p?.total ?? 0,
        page: p?.page ?? 1,
        limit,
        totalPages: p?.totalPages ?? 0,
        hasNextPage: p?.hasNextPage ?? false,
        hasPrevPage: p?.hasPrevPage ?? false,
      },
      categoryCounts: body.meta?.categoryCounts ?? {},
      featured: body.meta?.featured ?? null,
    };
  }

  /** Platform-admin blog listing (all statuses). */
  async getAdminBlogPosts(params?: {
    category?: string;
    q?: string;
    status?: string;
    sort?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    posts: BlogPostDto[];
    pagination: BlogListPagination;
    statusCounts: Record<string, number>;
    categoryCounts: Record<string, number>;
    topicCount: number;
  }> {
    const response: AxiosResponse<
      ApiResponse<BlogPostDto[]> & {
        pagination?: BlogListPagination;
        meta?: {
          statusCounts?: Record<string, number>;
          categoryCounts?: Record<string, number>;
          topicCount?: number;
        };
      }
    > = await this.api.get('/blogs/admin', {
      params: {
        ...params,
        limit: params?.limit ?? 10,
        page: params?.page ?? 1,
      },
    });
    const body = response.data;
    const p = body.pagination;
    return {
      posts: body.data ?? [],
      pagination: {
        total: p?.total ?? 0,
        page: p?.page ?? 1,
        limit: p?.limit ?? 10,
        totalPages: p?.totalPages ?? 0,
        hasNextPage: p?.hasNextPage ?? false,
        hasPrevPage: p?.hasPrevPage ?? false,
      },
      statusCounts: body.meta?.statusCounts ?? {},
      categoryCounts: body.meta?.categoryCounts ?? {},
      topicCount: body.meta?.topicCount ?? 0,
    };
  }

  async getBlogPostBySlug(slug: string): Promise<BlogPostDto> {
    const response: AxiosResponse<ApiResponse<BlogPostDto>> = await this.api.get(
      `/blogs/slug/${encodeURIComponent(slug)}`
    );
    return response.data.data!;
  }

  /** Platform admin: load any post by slug (including drafts). */
  async getAdminBlogPostBySlug(slug: string): Promise<BlogPostDto> {
    const response: AxiosResponse<ApiResponse<BlogPostDto>> = await this.api.get(
      `/blogs/admin/slug/${encodeURIComponent(slug)}`
    );
    return response.data.data!;
  }

  /**
   * Presigned S3 PUT for a single blog featured image (authenticated blog editors only).
   */
  async getBlogFeaturedPresignedUploadUrls(contentTypes?: string[]): Promise<{
    uploads: { uploadUrl: string; key: string; finalUrl: string; displayUrl?: string | null }[];
  }> {
    const response: AxiosResponse<
      ApiResponse<{ uploads: { uploadUrl: string; key: string; finalUrl: string; displayUrl?: string | null }[] }>
    > = await this.api.post('/blogs/presigned-featured-upload-urls', {
      contentTypes: contentTypes ?? []
    });
    const data = response.data.data!;
    return { uploads: data.uploads ?? [] };
  }

  async createBlogPost(payload: BlogPostPayload): Promise<BlogPostDto> {
    const response: AxiosResponse<ApiResponse<BlogPostDto>> = await this.api.post('/blogs', payload);
    return response.data.data!;
  }

  async updateBlogPost(id: string, payload: BlogPostPayload): Promise<BlogPostDto> {
    const response: AxiosResponse<ApiResponse<BlogPostDto>> = await this.api.put(
      `/blogs/${encodeURIComponent(id)}`,
      payload
    );
    return response.data.data!;
  }

  async deleteBlogPost(id: string): Promise<void> {
    await this.api.delete(`/blogs/${encodeURIComponent(id)}`);
  }

  async submitBusinessChallengeEntry(payload: {
    challenge?: string;
    fullName: string;
    accountEmail: string;
    businessName: string;
    businessStage: 'new' | 'launching' | 'switching';
    businessUrl: string;
    primaryService: string;
    progressStory: string;
    prizePlan: string;
    supportingUrl?: string;
    privacyConsent: boolean;
  }): Promise<{ id: string; createdAt: string }> {
    const response: AxiosResponse<ApiResponse<{ id: string; createdAt: string }>> = await this.api.post(
      '/business-challenge/submit',
      payload
    );
    return response.data.data!;
  }
}

export const apiService = new ApiService();
export default apiService; 